import React from 'react';
import { Col, Input, Row } from 'reactstrap';
import { AccentButton } from '../controls/AccentButton';
import { AccentDurationUnbound } from '../controls/unbound/AccentDurationUnbound';
import { AccentTextUnbound } from '../controls/unbound/AccentTextUnBound';
import { ConvertDateFromUTC, ConvertDateToUTC, t } from '../services/HelperService';
import { AccentDialog } from './AccentDialog';

export const MAX_DURATION_MINUTES = 24 * 60;

export const toActivityTimeZone = (value, timeZone) => {
    if (!value) return null;
    return timeZone ? ConvertDateFromUTC(value, timeZone) : new Date(value);
};

export const fromActivityTimeZone = (value, timeZone) => {
    if (!value) return null;
    return timeZone ? ConvertDateToUTC(value, timeZone) : new Date(value);
};

export const toTimeValue = value => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

export const updateDateTime = (current, value) => {
    if (!value) return null;
    const date = current ? new Date(current) : new Date();
    const [hours, minutes] = value.split(":").map(Number);
    date.setHours(hours, minutes, 0, 0);
    return date;
};

export const getDurationMinutes = (start, end) => {
    if (!start || !end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startMinutes = (startDate.getHours() * 60) + startDate.getMinutes();
    const endMinutes = (endDate.getHours() * 60) + endDate.getMinutes();
    return (endMinutes - startMinutes + MAX_DURATION_MINUTES) % MAX_DURATION_MINUTES;
};

export const alignEndToStart = (start, end) => {
    if (!start || !end) return end ?? null;

    const startDate = new Date(start);
    const selectedEnd = new Date(end);
    const alignedEnd = new Date(startDate);
    alignedEnd.setHours(selectedEnd.getHours(), selectedEnd.getMinutes(), 0, 0);

    if (alignedEnd < startDate) {
        alignedEnd.setDate(alignedEnd.getDate() + 1);
    }

    return alignedEnd;
};

export const addMinutes = (start, minutes) => {
    if (!start || minutes === null) return null;
    return new Date(new Date(start).getTime() + (minutes * 60000));
};

export const EditTimesDlg = ({ onClose, attendees = [], times = {}, appointmentStart = null, timeZone = null, defaultEnd = null, onSave }) => {

    const [draft, setDraft] = React.useState(() => {
        const zonedAppointmentStart = toActivityTimeZone(appointmentStart, timeZone);
        const zonedDefaultEnd = toActivityTimeZone(defaultEnd, timeZone);

        return attendees.map(a => {
            const savedTime = Array.isArray(times)
                ? times.find(time => time.RepID === a.RepID)
                : times[a.RepID];
            const start = toActivityTimeZone(savedTime?.Start ?? a.Start, timeZone) ?? zonedAppointmentStart;
            const savedEnd = toActivityTimeZone(savedTime?.End ?? a.End, timeZone);
            const end = savedEnd ? alignEndToStart(start, savedEnd) : zonedDefaultEnd;
            const durationMinutes = getDurationMinutes(start, end);
            return {
                RepID: a.RepID,
                Name: a.Name,
                Start: start,
                End: end,
                DurationMinutes: durationMinutes,
                DurationIsManual: false
            };
        });
    });

    const zonedAppointmentStart = React.useMemo(
        () => toActivityTimeZone(appointmentStart, timeZone),
        [appointmentStart, timeZone]
    );

    const updateStart = (repID, value) => {
        setDraft(prev => prev.map(row => {
            if (row.RepID !== repID) return row;

            if (row.DurationIsManual && row.DurationMinutes !== null) {
                return {
                    ...row,
                    Start: value,
                    End: addMinutes(value ?? zonedAppointmentStart, row.DurationMinutes)
                };
            }

            const effectiveStart = value ?? zonedAppointmentStart;
            const end = alignEndToStart(effectiveStart, row.End);
            const durationMinutes = getDurationMinutes(effectiveStart, end);
            return {
                ...row,
                Start: value,
                End: end,
                DurationMinutes: durationMinutes
            };
        }));
    };

    const updateEnd = (repID, value) => {
        setDraft(prev => prev.map(row => {
            if (row.RepID !== repID) return row;
            const start = row.Start ?? zonedAppointmentStart;
            const end = alignEndToStart(start, value);
            const durationMinutes = getDurationMinutes(start, end);
            return {
                ...row,
                End: end,
                DurationMinutes: durationMinutes,
                DurationIsManual: false
            };
        }));
    };

    const updateDuration = (repID, durationMinutes) => {
        setDraft(prev => prev.map(row => {
            if (row.RepID !== repID) return row;

            if (durationMinutes === null || !Number.isFinite(durationMinutes)) {
                return {
                    ...row,
                    DurationMinutes: null,
                    DurationIsManual: false
                };
            }

            return {
                ...row,
                DurationMinutes: durationMinutes,
                DurationIsManual: true,
                End: addMinutes(row.Start ?? zonedAppointmentStart, durationMinutes)
            };
        }));
    };

    const copyToAll = (repID, e) => {
        setDraft(prev => {
            const source = prev.find(row => row.RepID === repID);
            if (!source) return prev;

            return prev.map(row => ({
                ...row,
                Start: source.Start,
                End: source.End,
                DurationMinutes: source.DurationMinutes,
                DurationIsManual: source.DurationIsManual
            }));
        });
        e?.promise?.();
    };

    const save = (e) => {
        const result = draft.map(({ RepID, Name, Start, End }) => ({
            RepID,
            Name,
            Start: fromActivityTimeZone(Start, timeZone),
            End: fromActivityTimeZone(End, timeZone)
        }));
        onSave(result);
        onClose({ canceled: false, times: result });
        e?.promise?.();
    };

    return (
        <AccentDialog
            headerContent="Edit Times"
            onClose={() => onClose({ canceled: true })}
            relativeWidth={d => Math.min(d.width - 80, 900)}
            actionsContent={[
                <AccentButton key="save" outline={false} onClick={save}>{t("application_strings.application.buttons.save")}</AccentButton>,
            ]}
        >
            <div className="p-2">
                <Row className="mb-2 fw-bold">
                    <Col xs="2">Attendee</Col>
                    <Col xs="3">Start Time</Col>
                    <Col xs="3">End Time</Col>
                    <Col xs="2">Duration</Col>
                    <Col xs="2"></Col>
                </Row>
                {draft.map(row => (
                    <Row key={row.RepID} className="mb-2 align-items-center">
                        <Col xs="2">
                            <AccentTextUnbound disabled defaultValue={row.Name ?? ""} />
                        </Col>
                        <Col xs="3">
                            <Input
                                type="time"
                                value={toTimeValue(row.Start) ?? ""}
                                onChange={e => updateStart(row.RepID, updateDateTime(row.Start ?? zonedAppointmentStart, e.target.value))}
                            />
                        </Col>
                        <Col xs="3">
                            <Input
                                type="time"
                                value={toTimeValue(row.End) ?? ""}
                                onChange={e => updateEnd(row.RepID, updateDateTime(row.End ?? row.Start ?? zonedAppointmentStart, e.target.value))}
                            />
                        </Col>
                        <Col xs="2">
                            <AccentDurationUnbound
                                key={`${row.RepID}_duration`}
                                defaultValue={row.DurationMinutes}
                                maxMinutes={MAX_DURATION_MINUTES}
                                onChange={value => updateDuration(row.RepID, value)}
                            />
                        </Col>
                        <Col xs="2">
                            <AccentButton
                                outline={false}
                                disabled={draft.length < 2}
                                onClick={e => copyToAll(row.RepID, e)}
                            >
                                COPY TO ALL
                            </AccentButton>
                        </Col>
                    </Row>
                ))}
                {draft.length === 0 && (
                    <Row>
                        <Col xs="12">No attendees on this activity.</Col>
                    </Row>
                )}
            </div>
        </AccentDialog>
    );
};
